import {
  Bar,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  DataPopper,
  DataPopperRow,
  Stack,
  Typography,
  XAxis,
  YAxis,
} from '@cast/design-system';

import type { LatencyBucket } from '../_hooks/useReliabilityDashboardData';

type Props = {
  data: LatencyBucket[];
  isLoading?: boolean;
};

const getBarColor = (category: LatencyBucket['colorCategory']) => {
  switch (category) {
    case 'green':
      return 'green.500';
    case 'yellow':
      return 'yellow.500';
    case 'red':
      return 'red.500';
    default:
      return 'grey.300';
  }
};

export const P95LatencyDistributionChart = ({ data, isLoading }: Props) => {
  const total = data.reduce((sum, b) => sum + b.count, 0);

  return (
    <ChartContainer<LatencyBucket>
      data={data}
      isLoading={isLoading}
      sx={{ width: '100%', height: '100%' }}
      margin={{ top: 8, bottom: 24, left: 0, right: 0 }}
      testId="p95-latency-distribution-chart"
    >
      <ChartGrid hideVertical />
      <Bar<LatencyBucket>
        dataKey="count"
        getBarPropsByPoint={(payload) => ({
          fill: getBarColor(payload?.colorCategory ?? 'grey.300'),
        })}
        radius={4}
        // Keep sparse mid-range buckets visible when green dominates the domain
        minPointSize={4}
        activeBar={false}
      />
      <XAxis<LatencyBucket>
        dataKey="label"
        type="category"
        tick={{ fontSize: 10 }}
      />
      <YAxis
        tickCount={5}
        tickFormatter={(value: number) => `${value}`}
        allowDecimals={false}
      />
      <ChartTooltip<LatencyBucket>
        testId="p95-latency-distribution-tooltip"
        TooltipComponent={({ payload }) => {
          const bucket = payload?.[0]?.payload;
          if (!bucket) return null;
          const pct = total > 0 ? Math.round((bucket.count / total) * 100) : 0;
          return (
            <DataPopper
              size="small"
              testId="p95-latency-distribution-tooltip-root"
            >
              <DataPopperRow
                testId="p95-latency-distribution-tooltip-header"
                left={
                  <Typography variant="P10R" color="textSecondary">
                    {bucket.label.toUpperCase()}
                  </Typography>
                }
              />
              <DataPopperRow
                testId="p95-latency-distribution-tooltip-row"
                dividerTop
                indicator={{
                  color: getBarColor(bucket.colorCategory),
                  variant: 'filled',
                }}
                left={<Typography variant="P10R">Workloads</Typography>}
                right={
                  <Stack direction="row" gap={8}>
                    <Typography variant="P10R">{bucket.count}</Typography>
                    <Typography variant="P10R" color="textSecondary">
                      {pct}%
                    </Typography>
                  </Stack>
                }
              />
            </DataPopper>
          );
        }}
      />
    </ChartContainer>
  );
};
